/* RedBeryl · Roster Import — drag-and-drop the filled Soul Onboarding
 * Excel, preview every row (imported / updated / skipped with reasons),
 * then commit. Readiness checklist refreshes after commit. */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Gem, UploadCloud, CheckCircle2, AlertTriangle } from 'lucide-react';
import RedBerylReadinessTile from '@/components/RedBerylReadinessTile';

export default function RedBerylImportPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [readinessKey, setReadinessKey] = useState(0);
  const inputRef = useRef(null);

  const runDryRun = useCallback(async (f) => {
    setBusy(true); setPreview(null); setCommitted(null);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/redberyl/import-roster', fd);
      setPreview(r.data);
      toast.success(`Preview ready — ${r.data.imported + r.data.updated} rows will be written`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read the file');
      setFile(null);
    } finally { setBusy(false); }
  }, []);

  const onFile = useCallback((f) => {
    if (!f) return;
    if (!f.name.endsWith('.xlsx')) { toast.error('Please upload the .xlsx template'); return; }
    setFile(f);
    runDryRun(f);
  }, [runDryRun]);

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/redberyl/import-roster?commit=true', fd);
      setCommitted(r.data);
      setReadinessKey((k) => k + 1);
      toast.success(`Roster committed — ${r.data.imported} new · ${r.data.updated} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
    } finally { setBusy(false); }
  };

  useEffect(() => { document.title = 'RedBeryl · Roster Import'; }, []);

  const rows = preview?.members || [];
  const skipped = preview?.skipped || [];

  return (
    <div className="p-8 space-y-6 max-w-6xl" data-testid="redberyl-import-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: '#8B1A1A', fontFamily: 'Fraunces, serif' }}>
          <Gem size={26} /> RedBeryl · Roster Import
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Upload the filled Soul Onboarding template → preview every row → commit.
          Re-uploading the same file is safe (upserts by email).
        </p>
      </div>

      <RedBerylReadinessTile key={readinessKey} />

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed px-8 py-10 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#8B1A1A] bg-[#fdf3f3]' : 'border-[#d8c8c8] bg-white hover:bg-[#faf6f6]'
        }`}
        data-testid="redberyl-import-dropzone"
      >
        <UploadCloud size={34} className="mx-auto mb-2" color="#8B1A1A" />
        <div className="font-semibold text-[#1B2A4A]">
          {file ? file.name : 'Drop the filled RedBeryl-Soul-Onboarding-Template.xlsx here'}
        </div>
        <div className="text-[12px] text-[#9CA3AF] mt-1">or click to browse</div>
        <input
          ref={inputRef} type="file" accept=".xlsx" className="hidden"
          data-testid="redberyl-import-file-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>

      {busy && <div className="text-sm text-[#6B6B6B]" data-testid="redberyl-import-busy">Working…</div>}

      {/* Preview */}
      {preview && (
        <div className="space-y-4" data-testid="redberyl-import-preview">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#065f46]">
              {preview.imported} new · {preview.updated} updated
            </span>
            {skipped.length > 0 && (
              <span className="text-sm font-semibold text-[#92400e]">
                {skipped.length} skipped
              </span>
            )}
            {!committed && rows.length > 0 && (
              <button
                onClick={commit}
                disabled={busy}
                className="ml-auto px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: '#8B1A1A' }}
                data-testid="redberyl-import-commit-btn"
              >
                ✓ Commit {preview.imported + preview.updated} rows
              </button>
            )}
            {committed && (
              <span className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-[#065f46]" data-testid="redberyl-import-committed">
                <CheckCircle2 size={16} /> Committed to the roster
              </span>
            )}
          </div>

          {rows.length > 0 && (
            <div className="rounded-lg border border-[#e5e2dc] bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="redberyl-import-table">
                <thead className="bg-[#faf6f0] border-b border-[#e5e2dc]">
                  <tr>
                    {['Member', 'Email', 'Tier', 'LSM', 'Soul fields', 'Action'].map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-[#9CA3AF]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.email} className="border-b border-[#f0ede7] last:border-b-0">
                      <td className="px-4 py-2 font-medium">{m.name}</td>
                      <td className="px-4 py-2 text-[12px] text-[#6B6B6B]">{m.email}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                          m.tier === 'Black' ? 'bg-[#15110d] text-white' : 'bg-[#fdeaea] text-[#8B1A1A]'
                        }`}>{m.tier || '—'}</span>
                      </td>
                      <td className="px-4 py-2 text-[13px]">{m.lsm || '—'}</td>
                      <td className="px-4 py-2 text-[13px]">{m.soul_fields}</td>
                      <td className="px-4 py-2 text-[12px]">
                        <span className={m.action === 'imported' ? 'text-[#065f46]' : 'text-[#2A7B88]'}>
                          {m.action}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {skipped.length > 0 && (
            <div className="rounded-lg border border-[#fcd34d] bg-[#fef9ec] p-4" data-testid="redberyl-import-skipped">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[#92400e] mb-1.5">
                <AlertTriangle size={15} /> Skipped rows
              </div>
              <ul className="text-[13px] text-[#78350f] space-y-0.5">
                {skipped.map((s, i) => (
                  <li key={i}>Row {s.row}{s.email ? ` (${s.email})` : ''} — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
